import React, { useState } from "react";
import Card from "components/card";
import { toast, Toaster } from "react-hot-toast";
import { MdUpload, MdCheckCircle, MdError, MdDownload } from "react-icons/md";

const BulkReceivingUpload = () => {
  const [file, setFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [previewData, setPreviewData] = useState([]);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (selectedFile.type !== "text/csv") {
        toast.error("Please upload a CSV file", {
          icon: <MdError className="text-red-500" />,
          style: {
            background: "#FEE2E2",
            color: "#991B1B",
            border: "1px solid #FECACA",
          },
        });
        return;
      }
      setFile(selectedFile);
      previewCSV(selectedFile);
      toast.success("File selected successfully", {
        icon: <MdCheckCircle className="text-green-500" />,
        style: {
          background: "#D1FAE5",
          color: "#065F46",
          border: "1px solid #A7F3D0",
        },
      });
    }
  };

  const previewCSV = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const rows = text.split("\n");
      const headers = rows[0].split(",");
      const data = rows.slice(1, 6).map(row => {
        const values = row.split(",");
        return headers.reduce((obj, header, index) => {
          const value = values[index]?.trim();
          // Convert empty strings, 'nan', 'NaN', 'null' to null
          obj[header.trim()] = (!value || value.toLowerCase() === 'nan' || value.toLowerCase() === 'null') ? null : value;
          return obj;
        }, {});
      });
      setPreviewData(data);
    };
    reader.readAsText(file);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error("Please select a file to upload", {
        icon: <MdError className="text-red-500" />,
        style: {
          background: "#FEE2E2",
          color: "#991B1B",
          border: "1px solid #FECACA",
        },
      });
      return;
    }

    setIsLoading(true);
    const loadingToast = toast.loading("Uploading file...", {
      style: {
        background: "#EFF6FF",
        color: "#1E40AF",
        border: "1px solid #BFDBFE",
      },
    });

    try {
      // Read and process the file before upload
      const fileContent = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = (e) => reject(e);
        reader.readAsText(file);
      });

      const rows = fileContent.split("\n");
      const headers = rows[0].split(",");
      
      // Ensure organic_code is in headers
      if (!headers.includes('organic_code')) {
        headers.push('organic_code');
      }

      const processedRows = rows.slice(1).map(row => {
        const values = row.split(",");
        return headers.reduce((obj, header, index) => {
          const value = values[index]?.trim();
          const headerName = header.trim();
          
          // Special handling for organic_code - replace empty with "Null"
          if (headerName === 'organic_code') {
            obj[headerName] = (!value || value === '' || value.toLowerCase() === 'nan' || value.toLowerCase() === 'null') ? 'Null' : value;
          } else {
            // For other columns, keep existing logic
            obj[headerName] = (!value || value.toLowerCase() === 'nan' || value.toLowerCase() === 'null') ? null : value;
          }
          return obj;
        }, {});
      });

      // Create a new CSV with processed data
      const processedCSV = [
        headers.join(","),
        ...processedRows.map(row => 
          headers.map(header => {
            const headerName = header.trim();
            const value = row[headerName];
            // For organic_code, send as "Null" if empty
            if (headerName === 'organic_code') {
              return value === 'Null' ? 'Null' : value;
            }
            return value === null ? '' : value;
          }).join(",")
        )
      ].join("\n");

      const formData = new FormData();
      formData.append("file", new Blob([processedCSV], { type: "text/csv" }), file.name);

      const response = await fetch("https://usibras-web-backend.onrender.com/api/addAnalyseRCN", {
        method: "POST",
        body: formData,
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || "Upload failed");
      }

      toast.dismiss(loadingToast);
      toast.success(result.message || "File uploaded successfully", {
        icon: <MdCheckCircle className="text-green-500" />,
        style: {
          background: "#D1FAE5",
          color: "#065F46",
          border: "1px solid #A7F3D0",
        },
        duration: 5000,
      });
      
      setFile(null);
      setPreviewData([]);
    } catch (error) {
      toast.dismiss(loadingToast);
      toast.error(error.message || "Failed to upload file. Please try again.", {
        icon: <MdError className="text-red-500" />,
        style: {
          background: "#FEE2E2",
          color: "#991B1B",
          border: "1px solid #FECACA",
        },
        duration: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="mt-5 h-full">
      <Toaster position="top-right" />
      <Card extra={"w-full p-6 shadow-lg"}>
        <div className="mb-8 w-full">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-2xl font-bold text-navy-700 dark:text-white">
                Bulk Receiving Upload
              </h4>
              <p className="mt-2 text-base text-gray-600">
                Upload a CSV file containing RCN receiving records. The file should include all required fields for analysis and tracking.
              </p>
              <p className="mt-2 text-sm text-gray-500">
                Required columns: date, month, year, number_of_trucks, status_approval, agent, truck_number, reanalysis, region, province_or_city, country, count_per_kg, moisture, outturn, size_15, size_17, size_19, size_21, size_23, size_25, size_28, immature_percentage, spotted_percentage, total_sp_plus_im_percentage, total_defective_rate_percentage, expected_kernel_outturn, rcn_type, origin, quality_rcn, total_size, impurities, bored, void, oil, rotten, total_impurities
                <br />
                Optional columns: organic_code
              </p>
            </div>
            <a
              href="/assets/templates/sample_rcn_data_bulk.csv"
              download="sample_rcn_data_bulk.csv"
              className="flex items-center gap-2 rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white shadow-md hover:bg-brand-600 transition-all duration-200"
            >
              <MdDownload className="h-5 w-5" />
              Download Template
            </a>
          </div>
        </div>

        <form onSubmit={handleUpload} className="space-y-8">
          {/* File Upload Section */}
          <div className="rounded-xl border-2 border-dashed border-gray-300 p-8 dark:border-navy-700 bg-white dark:bg-navy-800 shadow-sm hover:shadow-md transition-all duration-200">
            <div className="flex flex-col items-center justify-center">
              <div className="rounded-full bg-brand-50 p-4 dark:bg-navy-700 mb-4">
                <MdUpload className="h-8 w-8 text-brand-500" />
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                Drag and drop your CSV file here, or click to select
              </p>
              <input
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                className="hidden"
                id="file-upload"
              />
              <label
                htmlFor="file-upload"
                className="cursor-pointer rounded-lg bg-brand-500 px-6 py-2.5 text-sm font-medium text-white shadow-md hover:bg-brand-600 transition-all duration-200"
              >
                Select File
              </label>
              {file && (
                <p className="mt-4 text-sm text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-navy-700 px-4 py-2 rounded-lg">
                  Selected file: {file.name}
                </p>
              )}
            </div>
          </div>

          {/* Preview Section */}
          {previewData.length > 0 && (
            <div className="rounded-xl bg-white dark:bg-navy-800 p-6 shadow-md">
              <h5 className="mb-6 text-lg font-semibold text-navy-700 dark:text-white">
                Preview (First 5 rows)
              </h5>
              <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-navy-600">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50 dark:bg-navy-700">
                      {Object.keys(previewData[0]).map((header) => (
                        <th
                          key={header}
                          className="px-6 py-3 text-left text-sm font-medium text-gray-600 dark:text-white"
                        >
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {previewData.map((row, index) => (
                      <tr
                        key={index}
                        className="border-b border-gray-200 dark:border-navy-600 hover:bg-gray-50 dark:hover:bg-navy-700 transition-colors duration-150"
                      >
                        {Object.values(row).map((value, i) => (
                          <td
                            key={i}
                            className="px-6 py-4 text-sm text-gray-600 dark:text-white"
                          >
                            {value}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Upload Button */}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={!file || isLoading}
              className={`rounded-lg px-6 py-2.5 text-sm font-medium text-white shadow-md transition-all duration-200 ${
                !file || isLoading
                  ? "cursor-not-allowed bg-gray-400"
                  : "bg-brand-500 hover:bg-brand-600"
              }`}
            >
              {isLoading ? "Uploading..." : "Upload File"}
            </button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default BulkReceivingUpload; 